import type { WorkflowStageTaskViewModel } from '@generated';
import React, { type ReactNode } from 'react';
import { getWorkflowStageTaskComponent } from './getWorkflowStageTaskComponent';
import type { SectionProps } from '~/shared/section/Section';
import type { QueryKey } from '@tanstack/react-query';

export interface WorkflowStageContextProviderProps {
	workflowStageTask: WorkflowStageTaskViewModel;
	sectionProps?: Omit<SectionProps, 'children'>;
	sectionWrapper?: (children: React.ReactNode, opts: { isResolvedView: boolean; disableSubmitOnResolved: boolean }) => React.ReactNode;
	invalidateQueryKeys?: QueryKey[];
	useModal?: boolean;
	onSuccess?: () => void;
}

interface WorkflowStageTaskContextValue extends Omit<WorkflowStageContextProviderProps, 'children'> {
	getStayId: () => string;
	getCertificationPeriodId: () => string;
	getVisitId: () => string;
	getSignatureId: () => string;
}

const WorkflowStageTaskContext = React.createContext<WorkflowStageTaskContextValue | null>(null);

export function WorkflowStageTaskProvider(props: WorkflowStageContextProviderProps): ReactNode {
	const getStayId = () => {
		if (!props.workflowStageTask.stayId) {
			throw new Error('Stay ID is required');
		}
		return props.workflowStageTask.stayId;
	};
	const getCertificationPeriodId = () => {
		if (!props.workflowStageTask.certificationPeriodId) {
			throw new Error('Certification period ID is required');
		}
		return props.workflowStageTask.certificationPeriodId;
	};
	const getVisitId = () => {
		if (!props.workflowStageTask.visitId) {
			throw new Error('Visit ID is required');
		}
		return props.workflowStageTask.visitId;
	};
	const getSignatureId = () => {
		if (!props.workflowStageTask.signatureId) {
			throw new Error('Signature ID is required');
		}
		return props.workflowStageTask.signatureId;
	};
	return (
		<WorkflowStageTaskContext.Provider value={{ ...props, getStayId, getCertificationPeriodId, getVisitId, getSignatureId }}>
			{getWorkflowStageTaskComponent(props.workflowStageTask)}
		</WorkflowStageTaskContext.Provider>
	);
}

export function useWorkflowStageTaskContext(): WorkflowStageTaskContextValue {
	const context = React.useContext(WorkflowStageTaskContext as React.Context<WorkflowStageTaskContextValue | null>);

	if (context === null) {
		throw new Error('useWorkflowStageTaskContext must be used within a WorkflowStageTaskProvider');
	}

	return context;
}
